import BoxInfo from '@/components/boxInfo/boxInfo';
import { ButtonClipBoard } from '@/components/buttonClipBoard/buttonClipBoard';
import DataGrid from '@/components/dataGrid/dataGrid';
import { PROCEDURE_TARGET_EDITION } from '@/modules/copay/entities/enums/procedures';
import {
  ProcedureToUpdate,
  UsageStatementProcess,
} from '@/modules/copay/entities/interfaces/usageStatements';
import { useCopayPermissions } from '@/modules/copay/hooks/useCopayPemissions/useCopayPermissions';
import { useProcedure } from '@/modules/copay/hooks/useProcedures/useProcedures';
import { getCountProceduresMessage, getFieldMissingMessage } from '@/modules/copay/utils/messages';
import { transformFormProceduresToPayload } from '@/modules/copay/utils/transformFormProceduresToPayload';
import ICONS from '@/shared/entities/constants/icons';
import { keyBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Icon, Text, TextField } from 'vkit/lib/components';
import { Grid, Tooltip } from 'vkit/lib/context';

interface ProcedureDescriptionProps {
  usageStatementProcess?: UsageStatementProcess;
  loading: boolean;
  callbackSuccess: () => void;
}

export const ProceduresDescription: React.FC<ProcedureDescriptionProps> = ({
  usageStatementProcess,
  loading,
  callbackSuccess,
}) => {
  const [formProcedures, setFormProcedures] = useState(
    keyBy<ProcedureToUpdate>(usageStatementProcess?.issueProcedures, 'id'),
  );

  const { updateMany, loading: updating } = useProcedure({
    callbackSuccess,
  });

  const { canAct } = useCopayPermissions({
    usageStatementProcess,
    target: PROCEDURE_TARGET_EDITION.DESCRIPTION,
  });

  const fieldsToUpdate: (keyof ProcedureToUpdate)[] = ['description'];
  const { fieldMissingMessage, countFieldMissing } = getFieldMissingMessage({
    fieldsByProcedureId: formProcedures,
    usageStatementProcess,
    fieldsToCount: fieldsToUpdate,
  });

  const updateDescription = useCallback((procedureId: number, description: string) => {
    setFormProcedures((current) => ({
      ...current,
      [procedureId]: {
        ...current[procedureId],
        description,
      },
    }));
  }, []);

  useEffect(() => {
    if (!usageStatementProcess?.descriptionAddedAt) {
      return;
    }

    usageStatementProcess?.issueProcedures?.forEach((procedure) => {
      if (procedure.description) {
        updateDescription(procedure.id, procedure.description);
      }
    });
  }, [
    updateDescription,
    usageStatementProcess?.descriptionAddedAt,
    usageStatementProcess?.issueProcedures,
  ]);

  return (
    <BoxInfo
      title='Correção do arquivo'
      icon={ICONS.MAGNIFIER}
      loading={loading}
      header={
        !usageStatementProcess?.descriptionAddedAt &&
        usageStatementProcess?.issueProcedures?.length ? (
          <Grid margin='0 -29px 0'>
            <Tooltip
              align='right'
              textAlign='left'
              title={`Para que o Time de Saúde possa analisar e finalizar a coparticipação, 
            é necessário adicionar uma descrição para os procedimentos sem rótulo. 
            Acesse o SIGE da Bradesco, busque o procedimento pelo código e preencha os campos de descrição.
            Assim que a descrição for inserida, o Time de Saúde fará a validação e aplicará os rótulos necessários.`}>
              <Grid alignItems='center' gap={8} padding='0 29px 0'>
                <Text
                  size='medium'
                  value={getCountProceduresMessage(usageStatementProcess)}
                  icon={ICONS.INFO}
                  iconColor='danger'
                  color='danger'
                  applyBaseColor
                  rounded
                  padding='8px'
                />
                <Icon name={ICONS.HELP} />
              </Grid>
            </Tooltip>
          </Grid>
        ) : null
      }
      footer={
        canAct && (
          <Grid alignContent='right' alignItems='center' gap={8}>
            <Text value={fieldMissingMessage} />

            <Button
              testId='copay-procedures-description-submit'
              label='Enviar'
              disabled={!canAct || countFieldMissing > 0}
              onClick={() =>
                updateMany(
                  transformFormProceduresToPayload({
                    updates: formProcedures,
                    fieldsToPayload: [...fieldsToUpdate, 'id'],
                  }),
                )
              }
              loading={updating}
            />
          </Grid>
        )
      }>
      <Grid column gap={16}>
        <DataGrid
          elevation={0}
          source={() => ({
            data: usageStatementProcess?.issueProcedures || [],
          })}
          columns={[
            {
              headerName: 'Código',
              render: ({ code }) => (
                <Grid gap={8} alignItems='center'>
                  {code}
                  <ButtonClipBoard text={String(code)} />
                </Grid>
              ),
            },
            {
              headerName: 'Adicionar descrição',
              render: ({ id }) => (
                <TextField
                  name={`procedure-description-${id}`}
                  clearable
                  small
                  value={formProcedures[id].description}
                  disabled={!canAct}
                  onChange={(value) => updateDescription(id, value)}
                />
              ),
            },
          ]}
        />
      </Grid>
    </BoxInfo>
  );
};
